<script>
import {checkpointDefinitionsArray} from "@modules/JourneyBuilder/Bricks/checkpoint-definitions";
import journeyBrickEndpointUuidMixin from '@modules/JourneyBuilder/Mixins/journey-brick-endpoint-uuid';
import journeyBrickEndpointsMixin from '@modules/JourneyBuilder/Mixins/journey-brick-endpoints'

import DragAndDropEndpoints from "@core/Components/DragAndDrop/DragAndDropEndpoints";

const ORIENTATIONS = {
    NONE: 'none',
    
    VERTICAL: 'vertical',
    HORIZONTAL: 'horizontal'
};

/**
 * Calculate the delta of two numbers
 * 
 * @param {number} left
 * @param {number} right
 * @return {number}
 */
function calculateDelta(left, right) {
    return Math.abs(left - right);
}

/**
 * Calculate the angle between the given connector and brick in degrees
 * 
 * @param {JourneyBrickConnection} connection
 * @param {JourneyBrick} brick
 * @return {number}
 */
function calculateAngleBetweenConnectorAndBrickInDegrees(connection, brick) {
    //https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/atan2
    //https://gist.github.com/conorbuck/2606166
    const degrees = Math.atan2(
        connection.connectorY - brick.y, 
        connection.connectorX - brick.x
    ) * 180 / Math.PI;
    
    //Our calculated angle may be negative, ensure our returned angle is positive
    //Round down for ease of use
    return Math.floor(
        Math.max(degrees, -degrees)
    );
}

export default {
    name: "brick-connection",
    mixins: [journeyBrickEndpointUuidMixin, journeyBrickEndpointsMixin],
    components: {
        DragAndDropEndpoints
    },
    render(createElement, context) {
        //Simple connection, brick -> brick
        if(!this.hasCheckpoints){
            return createElement('template');
        }

        //Complex connection, brick -> circle brick -> brick
        return createElement(DragAndDropEndpoints, {
            class: `orientation-${this.orientation}`,
            props: {
                jsPlumbInstance: this.jsPlumbInstance,
                referenceParameters: this.referenceParameters,
                endpointParameters: this.endpoints
            },
        }, this.$slots.default);
    },
    created() {
        const {
            id: connectionId,
            sourceBrickId,
            destinationBrickId
        } = this.connection;
        
        //Hide our endpoints
        this.referenceParameters.endpoint = 'Blank';

        this.brickSourceEndpointId = this.getBrickEndpointUuid(connectionId, sourceBrickId);
        this.brickDestinationEndpointId = this.getBrickEndpointUuid(connectionId, destinationBrickId);
        this.circleSourceEndpointId = this.getCircleEndpointUuid(connectionId, sourceBrickId);
        this.circleDestinationEndpointId = this.getCircleEndpointUuid(connectionId, destinationBrickId);

        //Connections should not be detacheable, they'll get removed once the user removes the circle brick
        this.endpoints = [{
            uuid: this.circleSourceEndpointId,
            isTarget: true,
            detachable: false
        }, {
            uuid: this.circleDestinationEndpointId,
            isSource: true,
            detachable: false
        }];
    },
    async mounted() {
        //In case of a newly created endpoint; we need to wait for our UI to update prior to it becoming usable
        await this.waitForUiUpdate();
        
        this.drawConnections(this.hasCheckpoints);
    },
    methods: {
        waitForUiUpdate(){
            return this.$nextTick();
        },
        drawConnections(hasCheckpoints) {
            //Either connect brick A with brick B
            //or connect brick A with $slot and brick B with $slot
            //dependent on whether or not our slot is rendered (= we have checkpoints)
            
            this.jsPlumbInstance.batch(() => {
                if(!hasCheckpoints){
                    //Our slot is not rendered; connect our two bricks directly
                    this.createJsPlumbConnection(
                        this.brickSourceEndpointId,
                        this.brickDestinationEndpointId
                    );
                } else {
                    //Our slot is rendered; connect our two bricks to our slot content
                    this.createJsPlumbConnection(
                        this.brickSourceEndpointId,
                        this.circleDestinationEndpointId
                    );

                    this.createJsPlumbConnection(
                        this.circleSourceEndpointId,
                        this.brickDestinationEndpointId
                    );
                }

                this.applyConnectorStrokeColor();
            });
        },
        createJsPlumbConnection(sourceEndpointId, destinationEndpointId){
            const jsPlumbConnection = this.jsPlumbInstance.connect({
                uuids: [
                    sourceEndpointId,
                    destinationEndpointId
                ]
            });

            //Make it easier for other components to obtain the id of the connection belonging to the jsplumb element
            jsPlumbConnection.canvas.setAttribute('data-jc-connection-id', this.connection.id);
            jsPlumbConnection.canvas.classList.add('jc-connection-line');
        },
        cleanConnections(){
            const sourceEndpoint = this.jsPlumbInstance.getEndpoint(this.brickSourceEndpointId);
            const destinationEndpoint = this.jsPlumbInstance.getEndpoint(this.brickDestinationEndpointId);
            
            //yeet
            if(sourceEndpoint) sourceEndpoint.deleteEveryConnection();
            if(destinationEndpoint) destinationEndpoint.deleteEveryConnection();
        },
        applyConnectorStrokeColor(){
            this.jsPlumbInstance.batch(() => {
                const sourceEndpoint = this.jsPlumbInstance.getEndpoint(this.brickSourceEndpointId);
                const destinationEndpoint = this.jsPlumbInstance.getEndpoint(this.brickDestinationEndpointId);

                [...sourceEndpoint.connections, ...destinationEndpoint.connections].forEach(connection => {
                    //Re-use our old style; we only want to re-color
                    //When two endpoints belong to the same element;
                    //JsPlumb might return the same style object for all endpoints
                    //Create a new object to ensure we don't screw ourselves
                    connection.setPaintStyle({
                        ...connection.getPaintStyle(),
                        stroke: this.connectorStroke
                    });
                });
            });
        }
    },
    watch: {
        async orientation() {
            if(!this.hasCheckpoints){
                return;
            }
            
            //Our element rotates around it's top left position
            //this may cause our connections to float upon orientation change
            //floating connections can be fixed by triggering a repaint
            await this.waitForUiUpdate();
            this.jsPlumbInstance.repaintEverything();
        },
        async 'connection.config'(){
            await this.waitForUiUpdate();

            //Repaint our UI to connect our connectors to the now resized element
            this.jsPlumbInstance.repaintEverything();
        },
        async hasCheckpoints(newValue){
            this.cleanConnections();
            
            //Our bricks may have been moved after creating the initial connection
            //attempt to move our connector to ~the center of it's source/destination 
            if(newValue){
                this.connection.connectorX = (this.connection.sourceBrick.x + this.connection.destinationBrick.x) / 2;
                this.connection.connectorY = (this.connection.sourceBrick.y + this.connection.destinationBrick.y) / 2;
            }
            
            await this.waitForUiUpdate();
            
            this.drawConnections(newValue);
        },
        connectorStroke(){
            this.applyConnectorStrokeColor();
        },
        checkpointCount(newCount, oldCount){
            //Attempt to keep our element centered
            
            //We are working with the assumption that:
            //* We always have child elements (newCount = 0 cannot occur)
            //* All child elements have an equal size
            //* All child elements have a width equal to their height
            
            let elementPositionIncrease = this.$el.firstElementChild.clientHeight / 2;
            
            //Move up/left
            if(newCount > oldCount) {
                elementPositionIncrease = -elementPositionIncrease;
            }
            
            if(this.orientation === ORIENTATIONS.VERTICAL){
                this.connection.connectorY += elementPositionIncrease;
                this.$el.style.top = `${this.connection.connectorY}px`;
            } else if (this.orientation === ORIENTATIONS.HORIZONTAL){
                this.connection.connectorX += elementPositionIncrease;
                this.$el.style.left = `${this.connection.connectorX}px`;
            }
        }
    },
    computed: {
        hasCheckpoints(){
            return checkpointDefinitionsArray
                .some(checkpoint => checkpoint.isActiveForConnection(this.connection));
        },
        connectorStroke(){
            return this.isActive ?
                this.enabledConnectorStroke :
                this.disabledConnectorStroke;
        },
        orientation(){
            if(!this.hasCheckpoints){
                return ORIENTATIONS.NONE;
            }
            
            // const deltaX = calculateDelta(
            //     calculateDelta(this.connection.connectorX, this.connection.sourceBrick.x),
            //     calculateDelta(this.connection.connectorX, this.connection.destinationBrick.x)
            // );
            //
            // const deltaY = calculateDelta(
            //     calculateDelta(this.connection.connectorY, this.connection.sourceBrick.y),
            //     calculateDelta(this.connection.connectorY, this.connection.destinationBrick.y)
            // );
            //
            // return deltaX > deltaY ? ORIENTATIONS.HORIZONTAL : ORIENTATIONS.VERTICAL;

            const sourceAngle = calculateAngleBetweenConnectorAndBrickInDegrees(this.connection, this.connection.sourceBrick);
            const destinationAngle = calculateAngleBetweenConnectorAndBrickInDegrees(this.connection, this.connection.destinationBrick);

            //Angle visualizer: https://rechneronline.de/winkel/angles.php
            if(
                (sourceAngle >= 0 && sourceAngle <= 45) ||
                (sourceAngle >= 135 && sourceAngle <= 225) ||
                (destinationAngle >= 0 && destinationAngle <= 45) ||
                (destinationAngle >= 135 && destinationAngle <= 225)
            ) {
                return ORIENTATIONS.HORIZONTAL;
            }

            return ORIENTATIONS.VERTICAL;
        },
        checkpointCount(){
            return checkpointDefinitionsArray
                .filter(checkpoint => checkpoint.isActiveForConnection(this.connection))
                .length;
        }
    },
    data: () => ({
        referenceParameters: null,
        brickSourceEndpointId: undefined,
        brickDestinationEndpointId: undefined,
        circleSourceEndpointId: undefined,
        circleDestinationEndpointId: undefined,
        
        endpoints: []
    }),
    props: {
        jsPlumbInstance: {
            type: Object,
            required: true
        },
        /**
         * @type {JourneyBrickConnection}
         */
        connection: {
            type: Object,
            required: true
        },
        isActive: {
            type: Boolean,
            required: true
        }
    }
}
</script>
<style lang="scss" scoped>
    .orientation-vertical {
        display: flex;
        flex-direction: column;
    }
    
    .orientation-horizontal {
        display: flex;
        flex-direction: row;
    }
</style>